import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  LinkDoc,
  SetLinkRequest,
} from '@trustero/trustero-api-web/lib/linker/linker_pb'
import { LinkerPromiseClient } from '@trustero/trustero-api-web/lib/linker/linker_grpc_web_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useUser } from 'src/components/async/Users'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { useModalState } from 'src/Modal/ModalStateContext'
import { documentSourceSpec } from './DocumentPicker.providers'
import type {
  DocumentNode,
  DocumentPickerRequest,
  DocumentPickerSelection,
} from './DocumentPicker.types'

export interface LinkSelectionOptions {
  caption?: string
  relevantDate?: Timestamp
  isViaRequest?: boolean
  /** Overrides the request's subject, e.g. an evidence group created later. */
  subjectIds?: string[]
  /** Overrides the selection, e.g. a subset the user kept. */
  nodes?: DocumentNode[]
}

export interface DocumentPickerContextValue {
  /** What the open (or last opened) picker was asked to pick. */
  request: DocumentPickerRequest | null
  /** Opens the picker for a receptor; the result lands in `selection`. */
  openPicker: (request: DocumentPickerRequest) => void
  /** The nodes the user added, until a consumer clears them. */
  selection: DocumentPickerSelection | null
  setSelection: (selection: DocumentPickerSelection | null) => void
  clearSelection: () => void
  /** Links the selection as evidence / policy documents through Linker.SetLink. */
  linkSelection: (options?: LinkSelectionOptions) => Promise<string[]>
}

const DocumentPickerContext = createContext<
  DocumentPickerContextValue | undefined
>(undefined)

export const DocumentPickerProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [request, setRequest] = useState<DocumentPickerRequest | null>(null)
  const [selection, setSelection] = useState<DocumentPickerSelection | null>(
    null,
  )
  const { openModal } = useModalState()
  const linkerClient = useAuthorizedGrpcClient(LinkerPromiseClient)
  const { data: user } = useUser()
  // Optional call: the provider is mounted app-wide, including in trees
  // whose tests stub the user fetch with a plain object.
  const userId = useMemo(() => user?.getId?.() || '', [user])

  const openPicker = useCallback(
    (next: DocumentPickerRequest): void => {
      setRequest(next)
      setSelection(null)
      openModal(ModalFormId.DOCUMENT_PICKER)
    },
    [openModal],
  )

  const clearSelection = useCallback((): void => setSelection(null), [])

  const linkSelection = useCallback(
    async (options: LinkSelectionOptions = {}): Promise<string[]> => {
      if (!selection) return []
      const nodes = options.nodes ?? selection.nodes
      if (nodes.length === 0) return []
      const { receptor, source } = selection
      const spec = documentSourceSpec(receptor.getModelid())
      const subjectIds =
        options.subjectIds ?? (request?.subjectId ? [request.subjectId] : [])

      const docs = nodes.map((node) => {
        let relevantDate = options.relevantDate
        if (!relevantDate && node.modifiedAt) {
          const parsed = new Date(node.modifiedAt)
          if (!Number.isNaN(parsed.getTime())) {
            relevantDate = Timestamp.fromDate(parsed)
          }
        }
        return new LinkDoc()
          .setImportKey(spec.linkImportKey(node, source))
          .setImportLink(node.url || '')
          .setSubjectIdsList(subjectIds)
          .setDocumentType(
            source === 'Policy'
              ? DOCUMENT_TYPE.POLICYDOC
              : DOCUMENT_TYPE.EVIDENCE,
          )
          .setRelevantDate(relevantDate)
          .setIsViaRequest(!!options.isViaRequest)
          .setCaption(options.caption?.trim() ? options.caption : node.title)
          .setFileName(node.title || node.id)
          .setReceptorModelId(receptor.getModelid())
          .setUserId(userId)
      })

      const response = await linkerClient.setLink(
        new SetLinkRequest()
          .setReceptorId(receptor.getId())
          .setDocumentsList(docs),
      )
      return response.getEvidenceIdsList()
    },
    [selection, request, linkerClient, userId],
  )

  const value = useMemo<DocumentPickerContextValue>(
    () => ({
      request,
      openPicker,
      selection,
      setSelection,
      clearSelection,
      linkSelection,
    }),
    [request, openPicker, selection, clearSelection, linkSelection],
  )

  return (
    <DocumentPickerContext.Provider value={value}>
      {children}
    </DocumentPickerContext.Provider>
  )
}

/**
 * The picker context when mounted, else undefined. For adapters that must
 * keep working in trees (and tests) that predate the provider.
 */
export const useDocumentPickerIfAvailable = ():
  | DocumentPickerContextValue
  | undefined => useContext(DocumentPickerContext)

export const useDocumentPicker = (): DocumentPickerContextValue => {
  const ctx = useContext(DocumentPickerContext)
  if (!ctx) {
    throw new Error(
      'useDocumentPicker must be used within DocumentPickerProvider',
    )
  }
  return ctx
}
